// ─── Types (mirror backend response shapes) ─────────────────────────────────

export type PlanId = 'free' | 'starter' | 'extensive' | 'unlimited';
export type BillingPeriod = 'weekly' | 'monthly' | 'yearly';
export type PlanStatus = 'pending' | 'active' | 'expired' | 'cancelled';
export type PaymentStatus = 'PENDING' | 'CONFIRMED' | 'FAILED' | 'REJECTED' | 'REFUNDED' | 'CANCELLED';

export interface UsageWindow {
  used: number;
  limit: number;
  remaining: number;
}

export interface UsageResponse {
  plan: PlanId;
  usage: {
    daily: UsageWindow;
    weekly: UsageWindow;
    monthly: UsageWindow;
    total: UsageWindow;
  };
}

export interface QuotaStatus {
  canUse: boolean;
  plan: PlanId;
  dailyUsed: number;
  dailyLimit: number;
  weeklyUsed: number;
  weeklyLimit: number;
  monthlyUsed: number;
  monthlyLimit: number;
  totalUsed: number;
  totalLimit: number;
}

export interface Subscription {
  id: string;
  plan: PlanId;
  billingPeriod: BillingPeriod | null;
  status: PlanStatus;
  startedAt: string | null;
  expiresAt: string | null;
  cryptoPaymentTxHash: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface SubscriptionStatusResponse {
  subscription: Subscription;
  effectivePlan: PlanId;
  usage: {
    daily: UsageWindow;
    weekly: UsageWindow;
    monthly: UsageWindow;
    total: UsageWindow;
  };
  quotaStatus: QuotaStatus;
}

export interface PlanPrice {
  planId: Exclude<PlanId, 'free'>;
  billingPeriod: BillingPeriod;
  amountCents: number;
  usdDisplay: string;
}

export interface PlanDefinition {
  id: PlanId;
  name: string;
  marketingName: string;
  tagline: string;
  isFree: boolean;
  billingPeriod: BillingPeriod | null;
  quotas: {
    daily: number;
    weekly: number;
    monthly: number;
    total: number | null;
  };
}

export interface PlansResponse {
  plans: PlanDefinition[];
  prices: PlanPrice[];
  paywallPromptThreshold: number;
  /** Anonymous (wallet-less) daily message allowance, served by the API. */
  guestDailyPromptLimit?: number;
}

export type PaymentCurrency = 'USDC' | 'USDT' | 'ETH';

export interface PaymentIntent {
  paymentId: string;
  plan: Exclude<PlanId, 'free'>;
  billingPeriod: BillingPeriod;
  amountCents: number;
  /** Amount in the selected currency's base units (wei for ETH) as a decimal string. */
  amountToken: string | null;
  usdDisplay: string;
  chainId: number;
  treasuryAddress: string;
  expiresAt: string;
  createdAt: string;
  selectedCurrency: PaymentCurrency;
  tokenAddress: string | null;
}

export interface PaymentRecord {
  id: string;
  plan: Exclude<PlanId, 'free'>;
  billingPeriod: BillingPeriod;
  amountCents: number;
  /** Amount in the selected currency's base units (wei for ETH) as a decimal string. */
  amountToken: string | null;
  currency: string;
  chainId: number;
  status: PaymentStatus;
  txHash: string | null;
  createdAt: string;
  confirmedAt: string | null;
  paymentIntentId: string | null;
}

// ─── Shared state ────────────────────────────────────────────────────────────
// State lives in SubscriptionProvider (contexts/SubscriptionContext.tsx) so
// every consumer (PlanBadge, SubscriptionStatus, SubscriptionTerminal, etc.)
// observes the same subscription data. This hook is a thin context consumer.

import { useSubscriptionContext } from '../contexts/SubscriptionContext';

export type SubscriptionState = {
  plans: PlansResponse | null;
  subscription: Subscription | null;
  usage: UsageResponse | null;
  quotaStatus: QuotaStatus | null;
  paymentHistory: PaymentRecord[];
  isLoadingPlans: boolean;
  isLoadingSubscription: boolean;
  isLoadingUsage: boolean;
  isLoadingPaymentHistory: boolean;
  error: string | null;
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useSubscription() {
  return useSubscriptionContext();
}
